import { Chess } from 'chess.js';
import { positionContext } from './position-identity.js';
import { INPUT_EVENT_TYPE, COLOR } from 'cm-chessboard';
import { LAST_MOVE_MARKER } from './board-markers.js';

const VARIATION_SELECTED_MARKER = { class: 'marker-variation-selected', slice: 'markerSquare' };

function isChess960Fen(fen) {
    if (!fen) return false;
    const castling = fen.trim().split(/\s+/)[2] || '-';
    return [...castling].some(c => !'KQkq-'.includes(c));
}

function createChess(fen, context) {
    // chess.js has no Chess960 rules: even no-rights 960 positions must not fall through.
    const namespace = positionContext(context);
    if (!namespace.supported || namespace.variant !== 'standard' || isChess960Fen(fen)) return null;
    if (!fen || typeof fen !== 'string') {
        return null;
    }
    try {
        return new Chess(fen);
    } catch (_) {
        return null;
    }
}

function normalizePromotion(value) {
    return String(value || '').trim().toLowerCase();
}

function moveToSidecarData(move, fenBefore, fenAfter) {
    if (!move || !fenBefore || !fenAfter) {
        return null;
    }
    const promotion = normalizePromotion(move.promotion);
    return {
        san: move.san || '',
        uci: `${move.from}${move.to}${promotion}`,
        from: move.from,
        to: move.to,
        promotion,
        fenBefore,
        fenAfter
    };
}

function movesMatch(a, b) {
    if (!a || !b) {
        return false;
    }
    return a.from === b.from
        && a.to === b.to
        && normalizePromotion(a.promotion) === normalizePromotion(b.promotion);
}

export class FelixChessVariationInputController {
    constructor(options = {}) {
        this.rootEl = options.rootEl || null;
        this.board = options.board || null;
        this.replay = options.replay || null;
        this.variationController = options.variationController || null;
        this.soundController = options.soundController || null;

        this.activeVariationId = '';
        this.activeParentPly = null;
        this.activeVariationPly = -1;
        this.activeFen = '';
        this.selectedSquare = null;
        this.suppressNextDestinationClick = false;
        this.isDestroyed = false;
        this.positionRevision = 0;
        this.pendingPosition = false;

        this.unsubscribePositionChange = null;
        this.unsubscribeGameLoaded = null;
        this.unsubscribeCleared = null;
        this.boundBoardClick = this.onBoardClick.bind(this);

        this.bindReplay();
        this.bindBoardClick();
        this.enableFromReplay();
    }

    bindReplay() {
        if (!this.replay || typeof this.replay.on !== 'function') {
            return;
        }
        this.unsubscribePositionChange = this.replay.on('positionchange', (state) => {
            if (state?.source === 'variation-exploration') {
                this.enableFromFen(state.currentFen || this.activeFen);
                return;
            }
            this.exitVariationMode();
            this.enableFromFen(state?.currentFen || '');
        });
        this.unsubscribeGameLoaded = this.replay.on('gameloaded', (state) => {
            this.exitVariationMode();
            this.enableFromFen(state?.currentFen || '');
        });
        this.unsubscribeCleared = this.replay.on('cleared', () => {
            this.clear();
        });
    }

    bindBoardClick() {
        const boardEl = this.getBoardElement();
        if (boardEl) {
            boardEl.addEventListener('click', this.boundBoardClick);
        }
    }

    enableFromReplay() {
        const state = this.replay && typeof this.replay.getState === 'function'
            ? this.replay.getState()
            : null;
        this.enableFromFen(state?.currentFen || '');
    }

    enableFromFen(fen) {
        if (this.isDestroyed || !this.board) {
            return;
        }

        const chess = createChess(fen, { ...this.replay?.getState()?.meta, ...this.rootEl?.__fcPositionContext });
        if (!chess || chess.isGameOver()) {
            this.disableMoveInput();
            return;
        }

        const color = chess.turn() === 'w' ? COLOR.white : COLOR.black;
        this.disableMoveInput();
        this.board.enableMoveInput((event) => this.handleMoveInputEvent(event), color);
    }

    handleMoveInputEvent(event) {
        switch (event.type) {
            case INPUT_EVENT_TYPE.moveInputStarted:
                this.suppressNextDestinationClick = true;
                this.markSelectedSquare(event.squareFrom);
                return true;

            case INPUT_EVENT_TYPE.validateMoveInput:
                this.suppressNextDestinationClick = true;
                return this.tryPlayMove({
                    from: event.squareFrom,
                    to: event.squareTo,
                    promotion: 'q'
                });

            case INPUT_EVENT_TYPE.moveInputFinished:
            case INPUT_EVENT_TYPE.moveInputCanceled:
                this.clearSelectedMarker();
                break;
        }
        return undefined;
    }

    tryPlayMove(inputMove) {
        const fenBefore = this.getCurrentFen();
        const chess = createChess(fenBefore, { ...this.replay?.getState()?.meta, ...this.rootEl?.__fcPositionContext });
        if (!chess) {
            return false;
        }

        let result;
        try {
            result = chess.move({ from: inputMove.from, to: inputMove.to, promotion: inputMove.promotion || 'q' });
        } catch (_) { return false; }
        if (!result) {
            return false;
        }

        this.clearSelectedMarker();

        if (!this.activeVariationId && this.isNextMainlineMove(result, fenBefore)) {
            window.requestAnimationFrame(() => {
                if (this.isDestroyed || !this.replay) {
                    return;
                }
                this.disableMoveInput();
                this.replay.next('variation-exploration-mainline', true);
            });
            return true;
        }

        const fenAfter = chess.fen();
        const moveData = moveToSidecarData(result, fenBefore, fenAfter);
        if (!moveData) {
            return false;
        }

        if (!this.activeVariationId) {
            const parentPly = this.getCurrentParentPly();
            const variation = this.variationController?.addVariation(parentPly, moveData);
            if (!variation) {
                return false;
            }
            ++this.positionRevision;
        this.pendingPosition = false;
        this.activeVariationId = variation.id;
            this.activeParentPly = parentPly;
            this.activeVariationPly = 0;
        } else {
            const updated = this.variationController?.appendMove(this.activeVariationId, moveData);
            if (!updated) {
                return false;
            }
            this.activeVariationPly = Math.max(0, updated.moves.length - 1);
        }

        this.activeFen = fenAfter;
        if (this.rootEl) {
            this.rootEl.dataset.variationMode = 'true';
        }
        const revision = ++this.positionRevision;
        this.pendingPosition = true;
        this.replay?.emit('positionpending', {});
        window.requestAnimationFrame(() => {
            if (revision !== this.positionRevision || this.isDestroyed || !this.isActive()) return;
            this.pendingPosition = false;
            this.applyVariationPosition(fenAfter, result);
        });
        return true;
    }

    applyVariationPosition(fen, move) {
        if (this.isDestroyed || !this.board || !fen) {
            return;
        }

        this.disableMoveInput();
        this.board.setPosition(fen, true);
        this.showLastMoveHighlight(move?.from, move?.to);
        this.soundController?.playFromMove(move);
        this.emitVariationPositionChange(fen);
        this.enableFromFen(fen);
    }

    emitVariationPositionChange(fen) {
        if (!this.replay || typeof this.replay.emit !== 'function') {
            return;
        }
        const state = typeof this.replay.getState === 'function'
            ? this.replay.getState()
            : {};
        this.replay.emit('positionchange', {
            ...state,
            currentFen: fen,
            source: 'variation-exploration',
            variationActive: true,
            activeVariationId: this.activeVariationId,
            activeVariationParentPly: this.activeParentPly,
            activeVariationPly: this.activeVariationPly,
            activeVariationLength: this.getActiveVariationLength()
        });
    }

    loadVariationMove(variationId, moveIndex, animated = true) {
        const variation = this.getVariation(variationId);
        const index = Number(moveIndex);
        if (!variation || !Number.isInteger(index) || index < 0 || index >= variation.moves.length) {
            return false;
        }

        const move = variation.moves[index];
        if (!move?.fenAfter) {
            return false;
        }

        ++this.positionRevision;
        this.pendingPosition = false;
        this.activeVariationId = variation.id;
        this.activeParentPly = variation.parentPly;
        this.activeVariationPly = index;
        this.activeFen = move.fenAfter;
        if (this.rootEl) {
            this.rootEl.dataset.variationMode = 'true';
        }

        this.disableMoveInput();
        this.board?.setPosition(move.fenAfter, animated);
        this.showLastMoveHighlight(move.from, move.to);
        this.emitVariationPositionChange(move.fenAfter);
        this.enableFromFen(move.fenAfter);
        return true;
    }

    stepVariation(delta) {
        if (!this.isActive()) {
            return false;
        }

        const variation = this.getVariation(this.activeVariationId);
        if (!variation || !variation.moves.length) {
            return false;
        }

        const nextIndex = this.activeVariationPly + delta;
        if (nextIndex < 0) {
            this.returnToMainline(true);
            return true;
        }
        if (nextIndex >= variation.moves.length) {
            return true;
        }
        return this.loadVariationMove(variation.id, nextIndex, true);
    }

    goToVariationEnd() {
        if (!this.isActive()) {
            return false;
        }
        const variation = this.getVariation(this.activeVariationId);
        if (!variation || !variation.moves.length) {
            return false;
        }
        return this.loadVariationMove(variation.id, variation.moves.length - 1, true);
    }

    getVariation(variationId) {
        if (!this.variationController) {
            return null;
        }
        if (typeof this.variationController.getVariation === 'function') {
            return this.variationController.getVariation(variationId);
        }
        return this.variationController.getAllVariations?.()
            .find((variation) => variation.id === variationId) || null;
    }

    getActiveVariationLength() {
        const variation = this.getVariation(this.activeVariationId);
        return Array.isArray(variation?.moves) ? variation.moves.length : 0;
    }

    isNextMainlineMove(userMove, fenBefore) {
        const state = this.replay && typeof this.replay.getState === 'function'
            ? this.replay.getState()
            : null;
        const moves = Array.isArray(state?.moves) ? state.moves : [];
        const nextSan = moves[state?.currentIndex || 0];
        if (!nextSan) {
            return false;
        }

        const chess = createChess(fenBefore, { ...this.replay?.getState()?.meta, ...this.rootEl?.__fcPositionContext });
        const mainlineMove = chess ? chess.move(nextSan, { sloppy: true }) : null;
        return movesMatch(userMove, mainlineMove);
    }

    getCurrentFen() {
        if (this.activeVariationId && this.activeFen) {
            return this.activeFen;
        }
        const state = this.replay && typeof this.replay.getState === 'function'
            ? this.replay.getState()
            : null;
        return state?.currentFen || '';
    }

    getCurrentParentPly() {
        const state = this.replay && typeof this.replay.getState === 'function'
            ? this.replay.getState()
            : null;
        const value = Number(state?.currentIndex);
        return Number.isInteger(value) && value >= 0 ? value : 0;
    }

    onBoardClick(event) {
        if (this.isDestroyed || !this.board) {
            return;
        }
        if (this.suppressNextDestinationClick) {
            this.suppressNextDestinationClick = false;
            return;
        }
        if (this.selectedSquare) {
            return;
        }

        const squareEl = event.target && event.target.closest
            ? event.target.closest('[data-square]')
            : null;
        const boardEl = this.getBoardElement();
        if (!squareEl || !boardEl || !boardEl.contains(squareEl)) {
            return;
        }

        const square = squareEl.getAttribute('data-square');
        const move = this.findUniqueDestinationMove(square);
        if (!move) {
            return;
        }
        this.tryPlayMove({
            from: move.from,
            to: move.to,
            promotion: move.promotion || (String(move.flags || '').includes('p') ? 'q' : undefined)
        });
    }

    findUniqueDestinationMove(square) {
        const chess = createChess(this.getCurrentFen(), { ...this.replay?.getState()?.meta, ...this.rootEl?.__fcPositionContext });
        if (!chess || !square) {
            return null;
        }
        const moves = chess.moves({ verbose: true })
            .filter(move => move.to === square);

        if (moves.length === 1) {
            return moves[0];
        }

        const promotionMoves = moves.filter(move => !!move.promotion);
        if (promotionMoves.length > 1 && promotionMoves.length === moves.length) {
            const queenPromotion = promotionMoves.filter(move => move.promotion === 'q');
            const sameMove = promotionMoves.every(move =>
                move.from === promotionMoves[0].from && move.to === promotionMoves[0].to);
            if (sameMove && queenPromotion.length === 1) {
                return queenPromotion[0];
            }
        }
        return null;
    }

    exitVariationMode() {
        ++this.positionRevision;
        this.pendingPosition = false;
        this.activeVariationId = '';
        this.activeParentPly = null;
        this.activeVariationPly = -1;
        this.activeFen = '';
        this.clearSelectedMarker();
        if (this.rootEl) {
            delete this.rootEl.dataset.variationMode;
        }
    }

    isActive() {
        return !!this.activeVariationId;
    }

    returnToMainline(animated = true) {
        if (!this.isActive()) {
            return;
        }
        const state = this.replay && typeof this.replay.getState === 'function'
            ? this.replay.getState()
            : null;
        const fen = state?.currentFen || '';
        this.exitVariationMode();
        this.disableMoveInput();
        if (this.replay && typeof this.replay.applyCurrentPosition === 'function') {
            this.replay.applyCurrentPosition(animated);
        } else if (fen && this.board) {
            this.board.setPosition(fen, animated);
            this.clearLastMoveHighlight();
        }
        if (this.replay && typeof this.replay.emit === 'function') {
            this.replay.emit('positionchange', {
                ...state,
                currentFen: fen,
                source: 'variation-exploration-return',
                variationActive: false
            });
        }
        this.enableFromFen(fen);
    }

    clear() {
        this.exitVariationMode();
        this.clearLastMoveHighlight();
        this.disableMoveInput();
    }

    disableMoveInput() {
        if (!this.board || typeof this.board.disableMoveInput !== 'function') {
            return;
        }
        try {
            this.board.disableMoveInput();
        } catch (_) {
            // cm-chessboard tolerates redundant disables in most versions, but
            // this controller should never break replay if input was not active.
        }
    }

    markSelectedSquare(square) {
        if (!this.board || !square) {
            return;
        }
        this.selectedSquare = square;
        this.board.removeMarkers(VARIATION_SELECTED_MARKER);
        this.board.addMarker(VARIATION_SELECTED_MARKER, square);
    }

    clearSelectedMarker() {
        this.selectedSquare = null;
        if (this.board) {
            this.board.removeMarkers(VARIATION_SELECTED_MARKER);
        }
    }

    showLastMoveHighlight(from, to) {
        if (!this.board) {
            return;
        }
        this.clearLastMoveHighlight();
        if (from) {
            this.board.addMarker(LAST_MOVE_MARKER, from);
        }
        if (to) {
            this.board.addMarker(LAST_MOVE_MARKER, to);
        }
    }

    clearLastMoveHighlight() {
        if (this.board) {
            this.board.removeMarkers(LAST_MOVE_MARKER);
        }
    }

    getBoardElement() {
        return this.rootEl?.querySelector('[data-fc-board]') || null;
    }

    destroy() {
        this.isDestroyed = true;
        this.clear();
        if (this.unsubscribePositionChange) {
            this.unsubscribePositionChange();
            this.unsubscribePositionChange = null;
        }
        if (this.unsubscribeGameLoaded) {
            this.unsubscribeGameLoaded();
            this.unsubscribeGameLoaded = null;
        }
        if (this.unsubscribeCleared) {
            this.unsubscribeCleared();
            this.unsubscribeCleared = null;
        }
        const boardEl = this.getBoardElement();
        if (boardEl) {
            boardEl.removeEventListener('click', this.boundBoardClick);
        }
        this.rootEl = null;
        this.board = null;
        this.replay = null;
        this.variationController = null;
        this.soundController = null;
    }
}
